import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { LineMask, FadeUp } from "@/components/Reveal";
import ContactSection from "@/components/ContactSection";
import { TrustLine } from "@/components/Formula";

const LINKEDIN_POST = "https://www.linkedin.com/posts/dimedial_agentur_newzer-medienmonitoring-reputationsmanagement-activity-7500267937346338816-g-7p";

const SECTIONS = [
  {
    h: "Wissen, bevor es wichtig wird.",
    p: [
      "Die Frühwarnzentrale für öffentliche Informationen von DIMEDIAL: beobachtet öffentliche Quellen rund um die Uhr, erkennt relevante Entwicklungen und meldet sich früh.",
      "NEWZER behält Personen, Unternehmen und Themen für dich im Blick – du musst nicht mehr selbst suchen.",
    ],
    link: { href: "https://newzer.de/", label: "NEWZER entdecken: newzer.de" },
  },
  {
    h: "Medienmonitoring. Reputationsmanagement. News-Alerts.",
    p: [
      "Drei Aufgaben, eine Plattform: Medienmonitoring, Reputationsmanagement für Marken und Verantwortliche, News-Alerts – DSGVO-orientiert.",
    ],
    list: ["Medienmonitoring", "Reputationsmanagement", "News-Alerts", "Themenbeobachtung"],
  },
  {
    h: "Für Unternehmen, Kommunen und Organisationen.",
    p: [
      "Wer öffentlich wahrgenommen wird, muss wissen, was über ihn gesagt wird. Unternehmen erkennen früh, wenn ihre Marke in Bewegung gerät; Kommunen behalten Debatten und Stimmungen im Blick, bevor daraus Krisen werden.",
    ],
  },
  {
    h: "Entwickelt von DIMEDIAL in Herford.",
    p: [
      "Konzipiert, gestaltet und programmiert von unserem Team in Herford – dieselbe Handschrift, mit der wir seit 2000 Film, Audio und Markenkommunikation bauen.",
      "Funktionen, Zugang und Einsatz: alles auf newzer.de.",
    ],
    link: { href: LINKEDIN_POST, label: "Launch-Ankündigung auf LinkedIn" },
    internalLink: { to: "/presse/dimedial-startet-newzer", label: "Pressemitteilung & Pressekit für Redaktionen" },
    internalLink2: { to: "/agentur", label: "Mehr über DIMEDIAL als Kreativagentur" },
  },
];

const Radar = () => (
  <div className="absolute right-[-24%] sm:right-[-8%] md:right-[3%] top-1/2 -translate-y-1/2 w-[80vw] sm:w-[55vw] md:w-[34vw] max-w-[520px] aspect-square opacity-60 pointer-events-none" aria-hidden="true">
    <svg viewBox="0 0 400 400" className="w-full h-full">
      {[60, 110, 160, 195].map((r) => (
        <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="rgba(245,245,240,0.09)" strokeWidth="1" />
      ))}
      <line x1="200" y1="200" x2="200" y2="5" stroke="rgba(203,161,83,0.45)" strokeWidth="1" className="radar-sweep" style={{ transformOrigin: "200px 200px" }} />
      <circle cx="200" cy="200" r="3.5" fill="#CBA153" />
      <circle cx="262" cy="128" r="2.5" fill="#CBA153" opacity="0.8" />
      <circle cx="148" cy="252" r="2" fill="#CBA153" opacity="0.55" />
      <circle cx="238" cy="292" r="2" fill="#CBA153" opacity="0.4" />
    </svg>
  </div>
);

const NewzerPage = () => (
  <>
    <Seo
      title="NEWZER – Frühwarnzentrale für Infos | von DIMEDIAL"
      description="NEWZER ist die von DIMEDIAL entwickelte Frühwarnzentrale für öffentliche Informationen: Medienmonitoring, Reputationsmanagement und News-Alerts für Unternehmen, Kommunen und Organisationen."
      path="/newzer"
      jsonLd={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: "https://dimedial.studio/" },
              { "@type": "ListItem", position: 2, name: "NEWZER", item: "https://dimedial.studio/newzer" },
            ],
          },
          {
            "@type": "SoftwareApplication",
            "@id": "https://newzer.de/#software",
            name: "NEWZER",
            url: "https://newzer.de/",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description: "NEWZER ist die von DIMEDIAL entwickelte Frühwarnzentrale für öffentliche Informationen. Die Plattform beobachtet öffentliche Quellen kontinuierlich, erkennt relevante Entwicklungen und informiert Unternehmen, Kommunen und Organisationen frühzeitig.",
            creator: { "@id": "https://dimedial.studio/#organization" },
            sameAs: [LINKEDIN_POST],
          },
        ],
      }}
    />

    <header className="relative min-h-[85vh] flex items-end overflow-hidden" data-testid="newzer-hero">
      <Radar />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/50" aria-hidden="true" />
      <div className="relative z-10 w-full shell pb-20 md:pb-24 pt-40">
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-grey">
            <li><Link to="/" data-testid="newzer-breadcrumb-home" className="hover:text-gold transition-colors duration-500">Start</Link></li>
            <li aria-hidden="true" className="text-gold">/</li>
            <li aria-current="page" className="text-paper/80">NEWZER</li>
          </ol>
        </nav>
        <p className="label-text text-gold mb-4">Eigenes Produkt von DIMEDIAL</p>
        <h1 className="font-display font-semibold uppercase tracking-tight leading-[0.95] text-[9vw] sm:text-6xl lg:text-8xl text-paper max-w-5xl" data-testid="newzer-h1">
          <LineMask delay={0.1}>NEWZER –</LineMask>
          <LineMask delay={0.22}>Frühwarnzentrale für öffentliche Informationen</LineMask>
        </h1>
        <FadeUp delay={0.35}>
          <p className="mt-8 text-lg md:text-xl text-grey max-w-xl">Wissen, bevor es wichtig wird.</p>
          <TrustLine className="mt-6" />
        </FadeUp>
      </div>
    </header>

    <div className="shell py-20 md:py-28">
      <div className="grid md:grid-cols-2 gap-5 md:gap-6" data-testid="newzer-sections">
        {SECTIONS.map((sec, i) => (
          <FadeUp key={sec.h} delay={i * 0.08}>
            <section className="h-full rounded-3xl border border-black/10 bg-white p-6 md:p-8 flex flex-col" data-testid={`newzer-section-${i}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#111]/50">0{i + 1}</p>
              <h2 className="mt-3 font-display font-semibold uppercase tracking-tight text-2xl md:text-3xl text-paper leading-tight">{sec.h}</h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-grey">{sec.p.join(" ")}</p>
              {sec.list && <ul className="mt-5 flex flex-wrap gap-2">{sec.list.map((item) => <li key={item} className="rounded-full border border-black/15 px-3 py-1 text-xs font-semibold text-paper">{item}</li>)}</ul>}
              <div className="mt-auto pt-6 flex flex-col gap-2">
                {sec.link && <a href={sec.link.href} target="_blank" rel="noopener noreferrer" data-testid={`newzer-section-link-${i}`} className="link-arrow">{sec.link.label}<ArrowUpRight size={18} className="arrow" /></a>}
                {sec.internalLink && <Link to={sec.internalLink.to} data-testid={`newzer-section-internal-${i}`} className="link-arrow">{sec.internalLink.label}<ArrowRight size={18} className="arrow" /></Link>}
                {sec.internalLink2 && <Link to={sec.internalLink2.to} data-testid={`newzer-section-internal2-${i}`} className="link-arrow">{sec.internalLink2.label}<ArrowRight size={18} className="arrow" /></Link>}
              </div>
            </section>
          </FadeUp>
        ))}
      </div>
    </div>

    <ContactSection />
  </>
);

export default NewzerPage;
